import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  UserPlus,
  Phone,
  Calendar,
  AlertCircle,
  Clock,
  UserX,
  TrendingUp,
  Plus,
  CalendarPlus,
  Activity,
  Target,
  CheckCircle,
  Filter
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { apiRequest, formatDateTime } from "../lib/utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList
} from "recharts";
import IntegrationStatusWidget from "../components/dashboard/IntegrationStatusWidget";

const FUNNEL_COLORS = {
  new_leads: "#3B82F6",
  contacted: "#6366F1",
  follow_up: "#8B5CF6",
  booked: "#22C55E",
  converted: "#10B981",
  lost: "#6B7280"
};

export const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedBranch]);

  const fetchBranches = async () => {
    try {
      const data = await apiRequest("/branches");
      setBranches(data || []);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const branchParam = selectedBranch !== "all" ? `?branch_id=${selectedBranch}` : "";
      const data = await apiRequest(`/dashboard/admin${branchParam}`);
      setStats(data);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse space-y-4 w-full max-w-4xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-200 rounded-xl"></div>)}
          </div>
        </div>
      </div>
    );
  }

  const funnelData = stats?.funnel
    ? [
        { name: "New", value: stats.funnel.new_leads, key: "new_leads" },
        { name: "Contacted", value: stats.funnel.contacted, key: "contacted" },
        { name: "Follow Up", value: stats.funnel.follow_up, key: "follow_up" },
        { name: "Booked", value: stats.funnel.booked, key: "booked" },
        { name: "Converted", value: stats.funnel.converted, key: "converted" },
        { name: "Lost", value: stats.funnel.lost, key: "lost" }
      ]
    : [];

  // Lead KPIs (from Meta Ads / Field Marketing)
  const leadKPIs = [
    {
      title: "Total Leads",
      value: stats?.total_leads || 0,
      icon: UserPlus,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      path: "/leads"
    },
    {
      title: "Today Leads",
      value: stats?.today_leads || 0,
      icon: Plus,
      color: "text-green-600",
      bgColor: "bg-green-50"
    },
    {
      title: "Pending Leads",
      value: stats?.pending_leads || 0,
      icon: Clock,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      path: "/leads"
    },
    {
      title: "Converted",
      value: stats?.converted_leads || 0,
      icon: CheckCircle,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50"
    }
  ];

  // SDR Summary (from Servall CRM)
  const sdrSummary = stats?.sdr_summary || { due: 0, overdue: 0, delayed: 0, no_show: 0 };

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header with Branch Filter and Quick Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground text-sm">Welcome back, {user?.name}</p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          {/* Branch Filter */}
          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger className="w-[160px]" data-testid="branch-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map((branch) => (
                <SelectItem key={branch.id} value={branch.id}>
                  {branch.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Quick Actions */}
          <Button 
            onClick={() => navigate("/leads")} 
            size="sm"
            className="bg-[#ED1C24] hover:bg-[#C41219]"
            data-testid="quick-add-lead"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Lead
          </Button>
          <Button 
            onClick={() => navigate("/customers")} 
            size="sm" 
            variant="outline"
            data-testid="quick-add-customer"
          >
            <UserPlus className="w-4 h-4 mr-1" />
            Add Customer
          </Button>
        </div>
      </div>

      {/* SECTION: Lead Metrics (Meta Ads / Field Marketing) */}
      <div>
        <h2 className="font-heading text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
          <UserPlus className="w-4 h-4" />
          Lead Metrics
          <span className="text-xs font-normal normal-case">(Meta Ads / Field Marketing)</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {leadKPIs.map((kpi) => (
            <Card
              key={kpi.title}
              className="bento-card cursor-pointer"
              onClick={() => kpi.path && navigate(kpi.path)}
              data-testid={`kpi-${kpi.title.toLowerCase().replace(/ /g, "-")}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{kpi.title}</p>
                    <p className="metric-value mt-1">{kpi.value.toLocaleString()}</p>
                  </div>
                  <div className={`p-2.5 rounded-xl ${kpi.bgColor}`}>
                    <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Lead Funnel Chart - Vertical Column Chart */}
      <Card className="bento-card" data-testid="lead-funnel">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#ED1C24]" />
            Lead Funnel
            <span className="text-xs font-normal text-muted-foreground">(New Leads Only)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={funnelData} 
                margin={{ top: 30, right: 20, left: 10, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis 
                  dataKey="name" 
                  fontSize={12} 
                  tickLine={false} 
                  axisLine={false}
                  tick={{ fill: '#6B7280' }}
                  interval={0}
                />
                <YAxis 
                  fontSize={12} 
                  tickLine={false} 
                  axisLine={false}
                  tick={{ fill: '#9CA3AF' }}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    fontSize: "12px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
                  }}
                  formatter={(value) => [value, "Count"]}
                />
                <Bar 
                  dataKey="value" 
                  radius={[6, 6, 0, 0]}
                  maxBarSize={60}
                >
                  {funnelData.map((entry) => (
                    <Cell key={entry.key} fill={FUNNEL_COLORS[entry.key]} />
                  ))}
                  <LabelList 
                    dataKey="value" 
                    position="top" 
                    fill="#374151"
                    fontSize={14}
                    fontWeight={600}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* SECTION: SDR Summary (Servall CRM) */}
      <div>
        <h2 className="font-heading text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
          <Target className="w-4 h-4" />
          SDR
          <span className="text-xs font-normal normal-case">(Servall CRM)</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card
            className="bento-card cursor-pointer status-card-due"
            onClick={() => navigate("/due")}
            data-testid="sdr-due"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Due</p>
                  <p className="metric-value mt-1">{sdrSummary.due}</p>
                </div>
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </CardContent>
          </Card>

          <Card
            className="bento-card cursor-pointer status-card-overdue"
            onClick={() => navigate("/overdue")}
            data-testid="sdr-overdue"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Overdue</p>
                  <p className="metric-value mt-1">{sdrSummary.overdue}</p>
                </div>
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
            </CardContent>
          </Card>

          <Card
            className="bento-card cursor-pointer status-card-delayed"
            onClick={() => navigate("/delayed")}
            data-testid="sdr-delayed"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Delayed</p>
                  <p className="metric-value mt-1">{sdrSummary.delayed}</p>
                </div>
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card
            className="bento-card cursor-pointer status-card-no-show"
            onClick={() => navigate("/no-show")}
            data-testid="sdr-no-show"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">No Show</p>
                  <p className="metric-value mt-1">{sdrSummary.no_show}</p>
                </div>
                <UserX className="w-6 h-6 text-red-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Activity & Integration Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Stats */}
        <Card className="bento-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">Today's Activity</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <Phone className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-lg font-bold font-heading tabular-nums">{stats?.calls_today || 0}</p>
                <p className="text-xs text-muted-foreground">Calls Made</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Calendar className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-lg font-bold font-heading tabular-nums">{stats?.bookings_today || 0}</p>
                <p className="text-xs text-muted-foreground">Bookings</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Users className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="text-lg font-bold font-heading tabular-nums">{stats?.total_customers || 0}</p>
                <p className="text-xs text-muted-foreground">Total Customers</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <Activity className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <p className="text-lg font-bold font-heading tabular-nums">{stats?.open_complaints || 0}</p>
                <p className="text-xs text-muted-foreground">Open Complaints</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Integration Status */}
        <IntegrationStatusWidget />
      </div>
    </div>
  );
};

export default AdminDashboard;
